import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { InfoDot } from "./infoDot";
import {
  Company,
  Identifier,
  UpdateCompanyPayload,
} from "../api/models/companies.models";
import useUpdateCompany from "../hooks/useUpdateCompany";
import useSynkkaIntegrations from "../hooks/useSynkkaIntegrations";

const SYNKKA_IDENTIFIER_TYPE_ID = 150;

interface SynkkaIdentifiersProps {
  company: Company | null;
  isAdmin: boolean;
  selectedCompanyId?: number;
}

const emptyIdArray: Identifier[] = [
  {
    identifierType: {
      id: 0,
      identifierFi: "",
      identifierSv: "",
      identifierEn: "",
    },
    text: "",
    id: 0,
  },
];

const Container = styled.div`
  margin-bottom: 2rem;
  margin-top: 0;
  padding: 0 1rem 1rem;
  background-color: #f8f9fa;
  border-radius: 8px;
  max-width: 400px;

  & h3 {
    font-size: 1.2rem;
    font-weight: 600;
    margin-top: 0;
    margin-bottom: 1rem;
  }
`;

const IdentifierRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
`;

const IdentifierText = styled.span`
  flex: 1;
  font-size: 0.95rem;
`;

const AddRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.75rem;
`;

const StyledInputText = styled(InputText)`
  flex: 1;
`;

const ImportButton = styled(Button)`
  color: var(--sumi-blue) !important;
`;

const UserImportButton = styled(Button)`
  margin-top: 1rem;
  margin-left: auto;
`;

const buildUpdatePayload = (
  company: Company,
  updatedIdentifiers: Identifier[],
): UpdateCompanyPayload => {
  const otherIdentifiers = company.identifiers.filter(
    (id) => id.identifierType.id !== SYNKKA_IDENTIFIER_TYPE_ID,
  );

  const identifiersArray = [...otherIdentifiers, ...updatedIdentifiers];

  return {
    id: company.id,
    companyName: company.companyName,
    contractStartDate: company.contractStartDate ?? undefined,
    contractEndDate: company.contractEndDate ?? undefined,
    country: company.country,
    email: company.email,
    phone: company.phone,
    identifiers:
      identifiersArray?.length === 0
        ? emptyIdArray // Have to hack it like this because backend doesn't accept empty array and we don't want to lose the other identifiers when adding/removing synkka identifiers
        : [...otherIdentifiers, ...updatedIdentifiers],
    addresses: company.addresses,
    organizations: (company.organizations ?? []).map((org) => ({
      id: org.id!,
      organizationName: org.organizationName,
      addresses: org.addresses ?? [],
      invoicingInformation: org.invoicingInformation!,
    })),
    invoicingInformation: company.invoicingInformation,
  };
};

export const SynkkaIdentifiers: React.FC<SynkkaIdentifiersProps> = ({
  company,
  isAdmin,
  selectedCompanyId,
}) => {
  const { t } = useTranslation();
  const { updateCompany, isUpdatingCompany } = useUpdateCompany({ isAdmin });
  const { importSynkkaProducts, userImportSynkkaProducts, isImportingSynkka } =
    useSynkkaIntegrations({
      isAdmin,
    });

  const [synkkaIdentifiers, setSynkkaIdentifiers] = useState<Identifier[]>([]);
  const [newIdentifierText, setNewIdentifierText] = useState("");

  useEffect(() => {
    if (!company) return;
    setSynkkaIdentifiers(
      company.identifiers.filter(
        (id) => id.identifierType.id === SYNKKA_IDENTIFIER_TYPE_ID,
      ),
    );
  }, [company]);

  if (!company) return null;

  const synkkaIdentifierType = synkkaIdentifiers[0]?.identifierType ?? {
    id: SYNKKA_IDENTIFIER_TYPE_ID,
  };

  const handleRemove = async (identifierId: number) => {
    const updated = synkkaIdentifiers.filter((id) => id.id !== identifierId);
    try {
      await updateCompany(buildUpdatePayload(company, updated));
      setSynkkaIdentifiers(updated);
    } catch {
      // error toast handled by hook
    }
  };

  const handleAdd = async () => {
    const trimmed = newIdentifierText.trim();
    if (!trimmed) return;

    const newIdentifier: Identifier = {
      identifierType: synkkaIdentifierType,
      text: trimmed,
    };
    const updated = [...synkkaIdentifiers, newIdentifier];
    try {
      await updateCompany(buildUpdatePayload(company, updated));
      setSynkkaIdentifiers(updated);
      setNewIdentifierText("");
    } catch {
      // error toast handled by hook
    }
  };

  return (
    <Container>
      {!isAdmin && (
        <InfoDot text={t("infotext-synkka-identifiers")} position="right">
          <h3 style={{ paddingTop: "1rem" }}>
            {t("label-synkka-identifiers")}
          </h3>
        </InfoDot>
      )}

      {synkkaIdentifiers.length === 0 && (
        <p style={{ color: "var(--text-color-secondary)", fontSize: "0.9rem" }}>
          {t("label-no-synkka-identifiers")}
        </p>
      )}

      {synkkaIdentifiers.map((identifier) => (
        <IdentifierRow key={identifier.id}>
          <IdentifierText>{identifier.text}</IdentifierText>
          {isAdmin && (
            <ImportButton
              id={`import-synkka-products-btn-${identifier.id}`}
              icon="pi pi-database"
              text
              severity="secondary"
              tooltip={t("cmd-synkka-import")}
              tooltipOptions={{ position: "top" }}
              onClick={() => importSynkkaProducts(identifier.text)}
              loading={isImportingSynkka}
              disabled={isUpdatingCompany}
            />
          )}
          <Button
            icon="pi pi-trash"
            text
            severity="danger"
            tooltip={t("cmd-remove")}
            tooltipOptions={{ position: "top" }}
            onClick={() => identifier?.id && handleRemove(identifier.id)}
            loading={isUpdatingCompany}
            disabled={isImportingSynkka}
          />
        </IdentifierRow>
      ))}

      <AddRow>
        <StyledInputText
          value={newIdentifierText}
          onChange={(e) => setNewIdentifierText(e.target.value)}
          placeholder={t("placeholder-synkka-identifier")}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleAdd();
          }}
          disabled={isUpdatingCompany}
        />
        <Button
          icon="pi pi-plus"
          label={t("cmd-add")}
          onClick={handleAdd}
          loading={isUpdatingCompany}
          disabled={!newIdentifierText.trim() || isImportingSynkka}
        />
      </AddRow>

      {!isAdmin && synkkaIdentifiers.length > 0 && (
        <AddRow>
          <UserImportButton
            icon="pi pi-database"
            label={t("cmd-synkka-import")}
            onClick={() =>
              userImportSynkkaProducts(selectedCompanyId as number)
            }
            loading={isImportingSynkka}
            iconPos="right"
          />
        </AddRow>
      )}
    </Container>
  );
};
